/**
 * Drawing toolbar — grouped tool ribbon for views, dimensions, annotations, and export.
 * Aesthetic: utilitarian precision — tight groups, subtle dividers, tool-state highlighting.
 */
import type { DrawingTool } from "./useAnnotationStore";

interface DrawingToolbarProps {
  activeTool: DrawingTool;
  onToolChange: (tool: DrawingTool) => void;
  generating: boolean;
  hasRunner: boolean;
  hasViews: boolean;
  onGenerateView: (dir: "front" | "top" | "right" | "iso") => void;
  onRegenerateAll: () => void;
  onExportPdf: () => void;
  onExportSvg: () => void;
  onExportDxf: () => void;
  onImportDxf: () => void;
}

interface ToolDef {
  tool: DrawingTool;
  label: string;
  title: string;
}

const DIM_TOOLS: ToolDef[] = [
  { tool: "linear", label: "Linear", title: "Linear dimension (point-to-point)" },
  { tool: "radial", label: "Radial", title: "Radial dimension" },
  { tool: "diameter", label: "⌀", title: "Diameter dimension" },
  { tool: "angular", label: "Angle", title: "Angular dimension" },
  { tool: "ordinate", label: "Ord", title: "Ordinate dimension" },
];

const ANN_TOOLS: ToolDef[] = [
  { tool: "centerline", label: "CL", title: "Centerline / center mark" },
  { tool: "leader", label: "Note", title: "Leader note" },
  { tool: "balloon", label: "Bal", title: "Balloon callout" },
  { tool: "text", label: "Txt", title: "Text note" },
  { tool: "gdt", label: "GD&T", title: "GD&T feature control frame" },
  { tool: "surfaceFinish", label: "Ra", title: "Surface finish symbol" },
];

const VIEW_DIRS: Array<{ dir: "front" | "top" | "right" | "iso"; label: string }> = [
  { dir: "front", label: "Front" },
  { dir: "top", label: "Top" },
  { dir: "right", label: "Right" },
  { dir: "iso", label: "Iso" },
];

export default function DrawingToolbar({
  activeTool, onToolChange, generating, hasRunner, hasViews,
  onGenerateView, onRegenerateAll, onExportPdf, onExportSvg, onExportDxf, onImportDxf,
}: DrawingToolbarProps) {
  return (
    <div className="dwg-toolbar">
      {/* Select pointer */}
      <div className="dwg-toolbar-group">
        <button
          className={`dwg-tool-btn ${activeTool === "select" ? "active" : ""}`}
          onClick={() => onToolChange("select")}
          title="Select / move annotations"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 2l3 12 2-4 4-2L3 2z"/></svg>
        </button>
      </div>

      <div className="dwg-toolbar-sep" />

      {/* Views */}
      <div className="dwg-toolbar-group">
        <span className="dwg-group-label">Views</span>
        {VIEW_DIRS.map(({ dir, label }) => (
          <button
            key={dir}
            className="dwg-tool-btn"
            onClick={() => onGenerateView(dir)}
            disabled={generating || !hasRunner}
            title={`Generate ${label} view`}
          >
            {label}
          </button>
        ))}
        <button
          className="dwg-tool-btn dwg-tool-icon"
          onClick={onRegenerateAll}
          disabled={generating || !hasRunner}
          title="Regenerate all views"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 8a6 6 0 0 1 10.5-4M14 8a6 6 0 0 1-10.5 4" />
            <path d="M12.5 1v3h-3M3.5 15v-3h3" />
          </svg>
        </button>
      </div>

      <div className="dwg-toolbar-sep" />

      {/* Dimensions */}
      <div className="dwg-toolbar-group">
        <span className="dwg-group-label">Dimensions</span>
        {DIM_TOOLS.map(({ tool, label, title }) => (
          <button
            key={tool}
            className={`dwg-tool-btn ${activeTool === tool ? "active" : ""}`}
            onClick={() => onToolChange(tool)}
            disabled={!hasViews}
            title={title}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dwg-toolbar-sep" />

      {/* Annotations */}
      <div className="dwg-toolbar-group">
        <span className="dwg-group-label">Annotate</span>
        {ANN_TOOLS.map(({ tool, label, title }) => (
          <button
            key={tool}
            className={`dwg-tool-btn ${activeTool === tool ? "active" : ""}`}
            onClick={() => onToolChange(tool)}
            disabled={!hasViews}
            title={title}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dwg-toolbar-sep" />

      {/* Export / Import */}
      <div className="dwg-toolbar-group">
        <span className="dwg-group-label">File</span>
        <button className="dwg-tool-btn" onClick={onExportPdf} title="Export PDF">PDF</button>
        <button className="dwg-tool-btn" onClick={onExportSvg} title="Export SVG">SVG</button>
        <button className="dwg-tool-btn" onClick={onExportDxf} title="Export DXF">DXF</button>
        <button className="dwg-tool-btn dwg-tool-import" onClick={onImportDxf} title="Import DXF file">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2v8M5 7l3 3 3-3M3 12h10"/></svg>
          Import
        </button>
      </div>

      {/* Status */}
      {generating && <span className="dwg-toolbar-status">Generating...</span>}
    </div>
  );
}
